import React from 'react';
import { Skeleton, styled } from '@mui/material';

const ExecutiveDashboardSkeleton: React.FC = () => {
  return (
    <SkeletonSurface>
      <KpiGridSkeleton>
        {[1, 2, 3, 4, 5, 6].map(item => (
          <KpiCardSkeleton key={item}>
            <KpiContentSkeleton>
              <Skeleton variant="text" width="62%" height={14} />
              <Skeleton variant="text" width="78%" height={28} />
              <Skeleton variant="text" width="54%" height={16} />
            </KpiContentSkeleton>
          </KpiCardSkeleton>
        ))}
      </KpiGridSkeleton>

      <InsightGridSkeleton>
        {[1, 2, 3, 4, 5].map(item => (
          <PanelSkeleton key={item} $wide={item === 1}>
            <PanelHeaderSkeleton>
              <div>
                <Skeleton variant="text" width={180} height={24} />
                <Skeleton variant="text" width={260} height={20} />
              </div>
              <Skeleton variant="rounded" width={34} height={34} />
            </PanelHeaderSkeleton>

            <PanelBodySkeleton>
              {[1, 2, 3].map(row => (
                <div key={row}>
                  <Skeleton variant="text" width="36%" height={18} />
                  <Skeleton variant="text" width="58%" height={30} />
                  <Skeleton variant="rounded" width="100%" height={8} />
                </div>
              ))}
            </PanelBodySkeleton>
          </PanelSkeleton>
        ))}
      </InsightGridSkeleton>
    </SkeletonSurface>
  );
};

const SkeletonSurface = styled('section')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const KpiGridSkeleton = styled('div')(({ theme }) => ({
  background: theme.palette.divider,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 6,
  display: 'grid',
  gap: 1,
  gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
  overflow: 'hidden',

  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  },

  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  },

  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

const KpiCardSkeleton = styled('article')(({ theme }) => ({
  background: '#fff',
  minHeight: 88,
  padding: theme.spacing(1.25, 1.4),
}));

const KpiContentSkeleton = styled('div')({
  minWidth: 0,
});

const InsightGridSkeleton = styled('div')(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(1.25),
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',

  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  },

  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
  },
}));

const PanelSkeleton = styled('section', {
  shouldForwardProp: prop => prop !== '$wide',
})<{ $wide?: boolean }>(({ theme, $wide }) => ({
  background: '#fff',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 6,
  gridColumn: $wide ? 'span 2' : 'auto',
  minHeight: 272,
  overflow: 'hidden',

  [theme.breakpoints.down('md')]: {
    gridColumn: 'auto',
  },
}));

const PanelHeaderSkeleton = styled('div')(({ theme }) => ({
  alignItems: 'center',
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  justifyContent: 'space-between',
  padding: theme.spacing(1.5),
}));

const PanelBodySkeleton = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.25),
  padding: theme.spacing(1.5),
}));

export default ExecutiveDashboardSkeleton;
